import { CHANGE_STATUS, READ_ONLY_ROLES, ROLE, TChangeV2, TRIAGE_ROLES, TRole } from '../models';

export const isReadOnlyRole = (role: TRole): boolean => READ_ONLY_ROLES.includes(role);

// The initiator (and admin, who sees all controls) may edit a request only while
// it is a Draft. A Returned request is a Draft again, so it is editable too. Once
// submitted, the request is read-only for the initiator.
export const canEditRequest = (role: TRole, change: TChangeV2): boolean => {
  if (isReadOnlyRole(role)) return false;
  if (change.status !== CHANGE_STATUS.DRAFT) return false;
  return role === ROLE.INITIATOR || role === ROLE.SYSTEM_ADMINISTRATOR;
};

// Change manager and quality assurance (and admin) can triage a submitted request.
export const canPerformTriage = (role: TRole, change: TChangeV2): boolean => {
  if (isReadOnlyRole(role)) return false;
  if (change.status !== CHANGE_STATUS.SUBMITTED) return false;
  return TRIAGE_ROLES.includes(role);
};

// Whether the triage tab should be visible at all. It is hidden until the request
// has been submitted, and only permitted roles ever see it.
export const isTriageTabVisible = (role: TRole, change: TChangeV2): boolean => {
  if (!TRIAGE_ROLES.includes(role)) return false;
  return change.status !== CHANGE_STATUS.DRAFT;
};

export const canComment = (role: TRole): boolean => !isReadOnlyRole(role);

export const canCreateChange = (role: TRole): boolean => !isReadOnlyRole(role);

// When triage is visible but not currently actionable, explain why rather than
// silently disabling with no reason.
export const getTriageBlockReason = (role: TRole, change: TChangeV2): string | undefined => {
  if (!TRIAGE_ROLES.includes(role)) return undefined;
  switch (change.status) {
    case CHANGE_STATUS.IN_ASSESSMENT:
      return 'This request has already been accepted for assessment.';
    case CHANGE_STATUS.CANCELLED:
      return 'This request is closed. Triage actions are no longer available.';
    case CHANGE_STATUS.DRAFT:
      return 'Triage becomes available once the request is submitted.';
    default:
      return undefined;
  }
};

// A read-only auditor, a signed-baseline demo, or a cancelled record all make the
// whole record non-editable regardless of the active tab.
export const isRecordReadOnly = (role: TRole, change: TChangeV2, signedBaseline: boolean): boolean =>
  signedBaseline || isReadOnlyRole(role) || change.status === CHANGE_STATUS.CANCELLED;
